import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getEmailCommsTemplates } from 'react-scale/corp/lib/api/growth';
import { TextField, Spinner, BaseIcon, Button } from '@scale/scaleui';
import TemplateCard from './TemplateCard';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import FilterComponent from 'react-scale/components/GrowthCenter/components/FilterComponent';
import SelectTag, {
  TemplatePlatformType,
} from 'react-scale/components/GrowthCenter/components/SelectTemplateTag';
import { EmailCommsTemplateStatus, EmailCommsTemplateTeam } from '@scale/shared-types';

const PAGE_SIZE = 12;

const TemplatesOverview = () => {
  const { isLoading, data: rawTemplates } = useQuery('get-email-comms-templates', () =>
    getEmailCommsTemplates({
      latest: true,
    }),
  );

  const templatesLibraryEnabled = useUnleashFeatureFlag('growth-template-library', {}, false);

  const location = useLocation();
  const history = useHistory();
  const [filterTerms, setFilterTerms] = useState<{ value: string; label: string }[]>([]);
  const [maxResults, setMaxResults] = useState(PAGE_SIZE);
  const queryParams = new URLSearchParams(location.search);
  const searchTerm = queryParams.get('searchTerm') || '';

  // Function to update searchTerm in URL
  const handleSearchChange = (value: string) => {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set('searchTerm', value);
    } else {
      params.delete('searchTerm');
    }
    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const templates = useMemo(() => {
    if (!rawTemplates) return [];
    let filteredTemplateData = rawTemplates;

    if (filterTerms.length > 0) {
      filteredTemplateData = filteredTemplateData.filter(template =>
        filterTerms.every(({ value }) =>
          (Object.values(TemplatePlatformType) as string[]).includes(value)
            ? template.fromName === value
            : (Object.values(EmailCommsTemplateStatus) as string[]).includes(value)
            ? template.status === value
            : (Object.values(EmailCommsTemplateTeam) as string[]).includes(value)
            ? template.team === value
            : true,
        ),
      );
    }

    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filteredTemplateData = filteredTemplateData.filter(
        template =>
          template.subject.toLowerCase().includes(searchTermLower) ||
          template.name.toLowerCase().includes(searchTermLower) ||
          template.body.toLowerCase().includes(searchTermLower) ||
          template.templateId.toLowerCase().includes(searchTermLower),
      );
    }
    return filteredTemplateData;
  }, [rawTemplates, searchTerm, filterTerms]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="flex items-center gap-5 justify-start">
          <TextField
            className="w-96"
            placeholder="Search templates by subject, body, sender content or ID"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            leadingIcon="search"
          />
          <FilterComponent
            filterMessage={
              filterTerms.length === 0 ? 'Filter' : filterTerms.map(term => term.label).join(', ')
            }
          >
            <SelectTag
              showTagId={false}
              value={filterTerms}
              onChange={setFilterTerms}
              multi={true}
              placeholder={'Filter by status or platform'}
            />
          </FilterComponent>
        </div>
        {templatesLibraryEnabled && (
          <Link to={Routes.TemplateSetup}>
            <Button size="lg" variant="primary">
              <BaseIcon contrastText edge="start" icon={['fas', 'plus']} />
              Create template
            </Button>
          </Link>
        )}
      </div>
      <div className="grid grid-cols-4 gap-4">
        {templates.slice(0, maxResults).map((template, index) => (
          <TemplateCard key={`template-card-${index}`} template={template} />
        ))}
      </div>

      <div className="flex w-full justify-center">
        {maxResults < templates.length && (
          <Button
            size="lg"
            variant="secondary"
            onClick={() => setMaxResults(maxResults + PAGE_SIZE)}
          >
            Load more
          </Button>
        )}
      </div>
    </>
  );
};

export default TemplatesOverview;
